import { MaterialBreakpointType } from '@/providers/global-grid-provider';
import { Theme } from '@rhg/libs/material';

export const STYLE_SECTION_DIVIDER = {
    my: (theme: Theme) => theme.ds.spacingUnits.SECTION,
};

export const STYLE_DYNAMIC_CARD = {
    height: '100%',
    width: '100%',
    '.MuiPaper-root': { height: '100%' },
};

interface ColumnSpanByBreakpointProps {
    currentMaterialBreakpoint: MaterialBreakpointType;
    expandedColumnSpan: number;
    isCentered?: boolean;
}

export const convertColumnAndGutterToItemWidth = ({
    columnSpan,
    columnWidth,
    gutter,
}: {
    columnSpan: number;
    columnWidth: number;
    gutter: number;
}) => {
    const clampedColumnSpan = Math.max(1, columnSpan);
    const totalGutterWidth = gutter * (clampedColumnSpan - 1);
    const totalColumnWidth = columnWidth * clampedColumnSpan;
    return totalColumnWidth + totalGutterWidth;
};

/**
 * The global grid system is based on a 12-column layout for desktop and larger screens.
 * This function adjusts the column span for different breakpoints to align with our design system.
 * This allows us to simplify the usage and only pass in the desktop column span value.
 */
export const convertColumnSpanByBreakpoint = ({
    currentMaterialBreakpoint,
    expandedColumnSpan,
    isCentered,
}: ColumnSpanByBreakpointProps) => {
    switch (currentMaterialBreakpoint) {
        case 'extraExtraLarge':
            // Subtract an extra column vs extraLarge to prevent span=4 cards (e.g. carousel sections)
            // from becoming oversized on very wide screens (>2500px). Reduces 4-column cards to 2
            // columns at this breakpoint without requiring per-component overrides.
            return Math.max(
                1,
                isCentered ? expandedColumnSpan - 4 : expandedColumnSpan - 2
            );
        case 'extraLarge':
            // For centered content, subtract 2 to maintain even margins on both sides.
            // For non-centered content, subtract 1 to align with design system constraints.
            return Math.max(
                1,
                isCentered ? expandedColumnSpan - 2 : expandedColumnSpan - 1
            );
        case 'large':
        case 'expanded':
            return Math.max(1, expandedColumnSpan);
        case 'medium':
            return Math.max(
                1,
                isCentered
                    ? expandedColumnSpan
                    : Math.ceil(expandedColumnSpan / 2)
            );
        case 'compact':
            return Math.max(1, Math.floor(expandedColumnSpan / 3));
        default:
            return Math.max(1, expandedColumnSpan);
    }
};

export const STYLE_BUTTON_AS_LINK = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    font: 'inherit',
    padding: 0,
    textAlign: 'left',
    textDecoration: 'none',
    height: 'min-content',
    minWidth: 'auto',
    verticalAlign: 'baseline',
    '&:hover': {
        background: 'none',
        textDecoration: 'underline',
    },
    // Visited styles use primary main (light/dark). These are the same as the link colors, and can be inherited from the usage.
};
